// src/store/trainersSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient"; // centralized client with baseURL + headers

const ENDPOINT = "/trainers/";

// Helper: Convert payload into FormData with resume file
const buildPayload = (data) => {
  const { trainerResumeFile, ...plain } = data;
  const fd = new FormData();

  if (trainerResumeFile instanceof File) {
    const finalPath = "/static/documents/trainer_resumes/" + trainerResumeFile.name;
    fd.append("trainerResume", finalPath);
    fd.append("trainerResumeFile", trainerResumeFile);
  } else {
    fd.append("trainerResume", plain.trainerResume || "");
  }

  Object.entries(plain).forEach(([k, v]) => {
    if (k !== "trainerResume") fd.append(k, v ?? "");
  });

  return fd;
};

/* ─────────────── Thunks ─────────────── */

// Fetch all trainers
export const fetchTrainers = createAsyncThunk("trainers/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get(ENDPOINT);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Insert new trainer
export const insertTrainer = createAsyncThunk("trainers/insert", async (trainer, { rejectWithValue }) => {
  try {
    const body = buildPayload(trainer);
    const res = await apiClient.post(ENDPOINT, body, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Update trainer
export const updateTrainer = createAsyncThunk("trainers/update", async (trainer, { rejectWithValue }) => {
  if (!trainer.id) return rejectWithValue("Missing trainer ID");
  try {
    const body = buildPayload(trainer);
    const res = await apiClient.put(`${ENDPOINT}${trainer.id}/`, body, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Delete trainer
export const deleteTrainer = createAsyncThunk("trainers/delete", async (id, { rejectWithValue }) => {
  try {
    await apiClient.delete(`${ENDPOINT}${id}/`);
    return id;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

/* ─────────────── Slice ─────────────── */

const trainersSlice = createSlice({
  name: "trainers",
  initialState: { trainers: [], loading: false, error: null },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchTrainers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrainers.fulfilled, (state, action) => {
        state.loading = false;
        state.trainers = action.payload;
      })
      .addCase(fetchTrainers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Insert
      .addCase(insertTrainer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(insertTrainer.fulfilled, (state, action) => {
        state.loading = false;
        state.trainers.push(action.payload);
      })
      .addCase(insertTrainer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateTrainer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrainer.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.trainers.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) {
          state.trainers[idx] = action.payload;
        }
      })
      .addCase(updateTrainer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteTrainer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTrainer.fulfilled, (state, action) => {
        state.loading = false;
        state.trainers = state.trainers.filter((t) => t.id !== action.payload);
      })
      .addCase(deleteTrainer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default trainersSlice.reducer;
